import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import ListManager from "@/components/ListManager";
import { Plus, Pencil, Trash2, Check, X, UserPlus, Shield } from "lucide-react";
import { toast } from "sonner";

const COLORS = ["#F59E0B", "#06B6D4", "#8B5CF6", "#EF4444", "#10B981", "#EC4899", "#3B82F6"];

export default function Settings() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  // categories
  const [categories, setCategories] = useState([]);
  const [name, setName] = useState("");
  const [color, setColor] = useState(COLORS[0]);
  const [editing, setEditing] = useState(null);
  const [editName, setEditName] = useState("");
  const [editColor, setEditColor] = useState("");

  // team
  const [team, setTeam] = useState([]);
  const [invite, setInvite] = useState({ name: "", email: "", password: "", role: "editor" });
  const [inviteOpen, setInviteOpen] = useState(false);

  const loadCats = () => api.get("/categories").then((r) => setCategories(r.data));
  const loadTeam = () => api.get("/team").then((r) => setTeam(r.data));
  useEffect(() => { loadCats(); loadTeam(); }, []);

  const add = async () => {
    if (!name.trim()) return toast.error("Category name required");
    await api.post("/categories", { name, color });
    toast.success("Category added");
    setName("");
    loadCats();
  };
  const saveEdit = async (id) => {
    await api.put(`/categories/${id}`, { name: editName, color: editColor });
    toast.success("Category updated");
    setEditing(null);
    loadCats();
  };
  const remove = async (id) => {
    await api.delete(`/categories/${id}`);
    toast.success("Category deleted");
    loadCats();
  };

  const sendInvite = async () => {
    if (!invite.name.trim() || !invite.email.trim() || !invite.password.trim()) return toast.error("All fields required");
    try {
      await api.post("/team/invite", invite);
      toast.success(`${invite.name} added to the team`);
      setInviteOpen(false);
      setInvite({ name: "", email: "", password: "", role: "editor" });
      loadTeam();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };
  const removeMember = async (id) => {
    try {
      await api.delete(`/team/${id}`);
      toast.success("Member removed");
      loadTeam();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const roleBadge = { admin: "bg-primary/15 text-primary border-primary/30", editor: "bg-cyan-500/15 text-cyan-400 border-cyan-500/30", member: "bg-slate-500/15 text-slate-400 border-slate-500/30" };
  const initials = (n) => (n || "U").split(" ").map((x) => x[0]).join("").slice(0, 2).toUpperCase();

  return (
    <div className="p-6 lg:p-10 max-w-4xl mx-auto animate-fade-up" data-testid="settings-page">
      <PageHeader title="Settings" subtitle="Manage your workspace configuration." />

      <Tabs defaultValue="categories">
        <TabsList className="mb-6 flex-wrap h-auto">
          <TabsTrigger value="categories" data-testid="tab-categories">Video Categories</TabsTrigger>
          <TabsTrigger value="languages" data-testid="tab-languages">Languages</TabsTrigger>
          <TabsTrigger value="video-types" data-testid="tab-video-types">Video Types</TabsTrigger>
          <TabsTrigger value="task-categories" data-testid="tab-task-categories">Task Categories</TabsTrigger>
          <TabsTrigger value="expense-categories" data-testid="tab-expense-categories">Expense Categories</TabsTrigger>
          <TabsTrigger value="team" data-testid="tab-team">Team</TabsTrigger>
        </TabsList>

        <TabsContent value="languages">
          <ListManager kind="languages" title="Video Languages" description="Languages available when tagging videos." placeholder="e.g. Korean" testidPrefix="languages" />
        </TabsContent>
        <TabsContent value="video-types">
          <ListManager kind="video-types" title="Video Types" description="Types like Short or Episode used to classify videos." placeholder="e.g. Trailer" testidPrefix="video-types" />
        </TabsContent>
        <TabsContent value="task-categories">
          <ListManager kind="task-categories" title="Task Categories" description="Categories used to organise tasks." placeholder="e.g. Thumbnail Design" showColor testidPrefix="task-categories" />
        </TabsContent>
        <TabsContent value="expense-categories">
          <ListManager kind="expense-categories" title="Expense Categories" description="Categories used in the Finances section (website, AI credits, email, etc.)." placeholder="e.g. Equipment" showColor testidPrefix="expense-categories" />
        </TabsContent>

        <TabsContent value="categories">
          <div className="glass border border-border rounded-2xl p-6">
            <h3 className="font-display font-semibold text-lg mb-1">Video Categories</h3>
            <p className="text-sm text-muted-foreground mb-6">Add, edit or remove the categories used to organise your video library.</p>

            <div className="flex flex-col sm:flex-row gap-3 mb-6 p-4 bg-secondary/30 rounded-xl">
              <Input value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()}
                placeholder="New category name" data-testid="add-category-input" className="flex-1" />
              <div className="flex items-center gap-1.5">
                {COLORS.map((c) => (
                  <button key={c} onClick={() => setColor(c)} data-testid={`color-swatch-${c}`}
                    className={`w-7 h-7 rounded-full transition-transform ${color === c ? "ring-2 ring-offset-2 ring-offset-background ring-white scale-110" : "hover:scale-110"}`}
                    style={{ background: c }} />
                ))}
              </div>
              <Button onClick={add} data-testid="add-category-button" className="font-semibold"><Plus className="w-4 h-4 mr-1.5" /> Add</Button>
            </div>

            <div className="space-y-2" data-testid="settings-category-table">
              {categories.length === 0 && <p className="text-sm text-muted-foreground text-center py-8">No categories yet.</p>}
              {categories.map((cat) => (
                <div key={cat.id} className="flex items-center gap-3 p-3 rounded-xl bg-secondary/40 border border-border" data-testid={`category-row-${cat.id}`}>
                  {editing === cat.id ? (
                    <>
                      <Input value={editName} onChange={(e) => setEditName(e.target.value)} className="flex-1 h-9" data-testid="edit-category-input" />
                      <div className="flex items-center gap-1.5">
                        {COLORS.map((c) => (
                          <button key={c} onClick={() => setEditColor(c)} className={`w-6 h-6 rounded-full ${editColor === c ? "ring-2 ring-white" : ""}`} style={{ background: c }} />
                        ))}
                      </div>
                      <Button size="icon" variant="ghost" onClick={() => saveEdit(cat.id)} data-testid="save-category-button" className="text-emerald-400"><Check className="w-4 h-4" /></Button>
                      <Button size="icon" variant="ghost" onClick={() => setEditing(null)}><X className="w-4 h-4" /></Button>
                    </>
                  ) : (
                    <>
                      <span className="w-4 h-4 rounded-full" style={{ background: cat.color }} />
                      <span className="flex-1 font-medium">{cat.name}</span>
                      <Button size="icon" variant="ghost" onClick={() => { setEditing(cat.id); setEditName(cat.name); setEditColor(cat.color); }} data-testid="edit-category-button" className="text-muted-foreground hover:text-foreground">
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button size="icon" variant="ghost" data-testid="delete-category-button" className="text-muted-foreground hover:text-destructive"><Trash2 className="w-4 h-4" /></Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent className="glass border-border">
                          <AlertDialogHeader>
                            <AlertDialogTitle>Delete "{cat.name}"?</AlertDialogTitle>
                            <AlertDialogDescription>Videos tagged with this category will keep their tag reference but it won't resolve to a name.</AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction onClick={() => remove(cat.id)} data-testid="confirm-delete-category" className="bg-destructive text-destructive-foreground hover:bg-destructive/90">Delete</AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </>
                  )}
                </div>
              ))}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="team">
          <div className="glass border border-border rounded-2xl p-6" data-testid="team-panel">
            <div className="flex items-start justify-between mb-6">
              <div>
                <h3 className="font-display font-semibold text-lg mb-1">Team Members</h3>
                <p className="text-sm text-muted-foreground">Invite editors so they can log in and get tasks assigned to them.</p>
              </div>
              {isAdmin && (
                <Button onClick={() => setInviteOpen(true)} data-testid="invite-member-button" className="font-semibold">
                  <UserPlus className="w-4 h-4 mr-1.5" /> Invite
                </Button>
              )}
            </div>
            {!isAdmin && <p className="text-xs text-muted-foreground mb-4">Only admins can invite or remove members.</p>}
            <div className="space-y-2" data-testid="team-list">
              {team.map((m) => (
                <div key={m.id} className="flex items-center gap-3 p-3 rounded-xl bg-secondary/40 border border-border" data-testid={`team-row-${m.id}`}>
                  <div className="w-9 h-9 rounded-full bg-gradient-to-br from-primary to-amber-600 flex items-center justify-center text-xs font-bold text-primary-foreground">{initials(m.name)}</div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">{m.name}</p>
                    <p className="text-xs text-muted-foreground truncate">{m.email}</p>
                  </div>
                  <Badge variant="outline" className={`capitalize ${roleBadge[m.role] || roleBadge.member}`}>
                    {m.role === "admin" && <Shield className="w-3 h-3 mr-1" />}{m.role}
                  </Badge>
                  {isAdmin && m.role !== "admin" && (
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button size="icon" variant="ghost" data-testid={`remove-member-${m.id}`} className="text-muted-foreground hover:text-destructive"><Trash2 className="w-4 h-4" /></Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent className="glass border-border">
                        <AlertDialogHeader>
                          <AlertDialogTitle>Remove {m.name}?</AlertDialogTitle>
                          <AlertDialogDescription>They will lose access to the workspace. Existing task assignments keep their name.</AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction onClick={() => removeMember(m.id)} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">Remove</AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  )}
                </div>
              ))}
            </div>
          </div>
        </TabsContent>
      </Tabs>

      <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
        <DialogContent className="glass border-border">
          <DialogHeader><DialogTitle className="font-display">Invite Team Member</DialogTitle></DialogHeader>
          <p className="text-xs text-muted-foreground -mt-2">Set their login now and share it — they can sign in immediately.</p>
          <div className="space-y-4">
            <div className="space-y-2"><Label>Name</Label><Input value={invite.name} onChange={(e) => setInvite({ ...invite, name: e.target.value })} data-testid="invite-name-input" placeholder="Alex Editor" /></div>
            <div className="space-y-2"><Label>Email</Label><Input type="email" value={invite.email} onChange={(e) => setInvite({ ...invite, email: e.target.value })} data-testid="invite-email-input" placeholder="alex@studio.com" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Temp Password</Label><Input value={invite.password} onChange={(e) => setInvite({ ...invite, password: e.target.value })} data-testid="invite-password-input" placeholder="Set a password" /></div>
              <div className="space-y-2"><Label>Role</Label>
                <Select value={invite.role} onValueChange={(v) => setInvite({ ...invite, role: v })}>
                  <SelectTrigger data-testid="invite-role-select"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="editor">Editor</SelectItem><SelectItem value="member">Member</SelectItem></SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter><Button onClick={sendInvite} data-testid="invite-submit" className="font-semibold">Add Member</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
